import { Box, CircularProgress } from '@mui/material';

import { FProgressBar } from '../../../../../../components/base';
import { FORTA_BLUE } from '../../../../../../utils/colors';
import { QuestionnaireSectionLabel } from '../../../../common';
import {
  hasFieldValue,
  shouldRevealQuestionnaireErrors,
  useQuestionnaireFormik,
} from '../../../../common/hooks';
import { intakeQuestionnaireActions } from '../../../../../../store';
import { INTAKE_TAB_IDS } from '../../../constants';
import { useIntakeMedicalInfoUpdateLoading, useIntakeQuestionnaireLoading } from '../../../hooks';
import {
  AdditionalDiagnosisList,
  AsdDiagnosisFields,
  BirthInfoFields,
  ChildDevelopmentFields,
  MedicalInfoFields,
  MedicalSelectionSections,
} from './components';
import {
  useAreScreeningDatesRequired,
  useIntakeMedicalDiagnoses,
  useIntakeMedicalSelections,
  useMedicalInfoInitialValues,
  useSaveMedicalInfoFieldOnBlur,
} from './hooks';
import { areIntakeMedicalDiagnosesComplete } from './utils';
import { MEDICAL_FORM_BOX_SX, MEDICAL_FORM_LOADING_OVERLAY_SX } from './styles';
import { getMedicalInfoSchema } from './validationSchema';

export default function MedicalForm() {
  const initialValues = useMedicalInfoInitialValues();
  const areScreeningDatesRequired = useAreScreeningDatesRequired();
  const isLoading = useIntakeQuestionnaireLoading();
  const isUpdating = useIntakeMedicalInfoUpdateLoading();
  const { diagnoses } = useIntakeMedicalDiagnoses();
  const {
    earlyInterventions,
    pregnancyIssues,
    regressions,
    isSaving: isSavingSelection,
    createSelection,
    deleteSelection,
  } = useIntakeMedicalSelections();

  const hasNonFormikSavedValues =
    earlyInterventions.length > 0 ||
    pregnancyIssues.length > 0 ||
    regressions.length > 0 ||
    diagnoses.length > 0;
  const formik = useQuestionnaireFormik({
    tabId: INTAKE_TAB_IDS.MEDICAL,
    initialValues,
    validationSchema: getMedicalInfoSchema(areScreeningDatesRequired),
    extraRequirementMet:
      earlyInterventions.length > 0 &&
      pregnancyIssues.length > 0 &&
      regressions.length > 0 &&
      areIntakeMedicalDiagnosesComplete(diagnoses),
    hasNonFormikSavedValues,
    updateTabStatus: intakeQuestionnaireActions.updateIntakeTabStatus,
  });
  const { saveFieldOnBlur } = useSaveMedicalInfoFieldOnBlur(formik, initialValues);
  const revealListErrors = shouldRevealQuestionnaireErrors({
    submitCount: formik.submitCount,
    touched: formik.touched,
    hasSavedValues: hasFieldValue(initialValues) || hasNonFormikSavedValues,
  });

  return (
    <>
      <QuestionnaireSectionLabel variant="h5" sx={{ mb: 1.5, color: FORTA_BLUE.primary }}>
        Medical history
      </QuestionnaireSectionLabel>
      <Box component="form" noValidate onSubmit={formik.handleSubmit} sx={MEDICAL_FORM_BOX_SX}>
        {isUpdating ? <FProgressBar /> : null}
        {isLoading ? (
          <Box sx={MEDICAL_FORM_LOADING_OVERLAY_SX}>
            <CircularProgress size={32} />
          </Box>
        ) : null}

        <MedicalInfoFields
          areScreeningDatesRequired={areScreeningDatesRequired}
          disabled={isLoading}
          formik={formik}
          saveFieldOnBlur={saveFieldOnBlur}
        />

        <QuestionnaireSectionLabel variant="h6">
          Child Autism spectrum disorder (F84.0) diagnosis
        </QuestionnaireSectionLabel>
        <AsdDiagnosisFields
          disabled={isLoading}
          formik={formik}
          saveFieldOnBlur={saveFieldOnBlur}
        />

        <QuestionnaireSectionLabel variant="h6">Additional diagnosis</QuestionnaireSectionLabel>
        <AdditionalDiagnosisList disabled={isLoading} />

        <QuestionnaireSectionLabel variant="h6">Birth information</QuestionnaireSectionLabel>
        <BirthInfoFields disabled={isLoading} formik={formik} saveFieldOnBlur={saveFieldOnBlur} />

        <QuestionnaireSectionLabel variant="h6">
          Early Childhood Development
        </QuestionnaireSectionLabel>
        <ChildDevelopmentFields
          disabled={isLoading}
          formik={formik}
          saveFieldOnBlur={saveFieldOnBlur}
        />

        <MedicalSelectionSections
          createSelection={createSelection}
          deleteSelection={deleteSelection}
          disabled={isLoading}
          earlyInterventions={earlyInterventions}
          isSavingSelection={isSavingSelection}
          pregnancyIssues={pregnancyIssues}
          regressions={regressions}
          revealListErrors={revealListErrors}
        />
      </Box>
    </>
  );
}
